import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export const ACCESSIBILITY_STORAGE_KEY = "zunoAcessibilidade";
export const DEFAULT_ACCESSIBILITY = Object.freeze({
  textoGrande: false,
  altoContraste: false,
  reduzirAnimacoes: false,
});

const AccessibilityContext = createContext(null);

export function readAccessibilityPreferences() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(ACCESSIBILITY_STORAGE_KEY) || "{}");
    return {
      textoGrande: stored.textoGrande === true,
      altoContraste: stored.altoContraste === true,
      reduzirAnimacoes: stored.reduzirAnimacoes === true,
    };
  } catch {
    return { ...DEFAULT_ACCESSIBILITY };
  }
}

export function applyAccessibilityPreferences(preferences) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.largeText = preferences.textoGrande ? "true" : "false";
  root.dataset.highContrast = preferences.altoContraste ? "true" : "false";
  root.dataset.reduceMotion = preferences.reduzirAnimacoes ? "true" : "false";
  root.style.setProperty("--zuno-font-scale", preferences.textoGrande ? "1.14" : "1");
}

export function initializeAccessibility() {
  const preferences = readAccessibilityPreferences();
  applyAccessibilityPreferences(preferences);
  return preferences;
}

export function AccessibilityProvider({ children }) {
  const [preferences, setPreferences] = useState(() => {
    const initial = readAccessibilityPreferences();
    applyAccessibilityPreferences(initial);
    return initial;
  });

  const setPreference = useCallback((key, enabled) => {
    if (!(key in DEFAULT_ACCESSIBILITY)) return;
    setPreferences(current => {
      const next = { ...current, [key]: enabled === true };
      try { window.localStorage.setItem(ACCESSIBILITY_STORAGE_KEY, JSON.stringify(next)); } catch {}
      applyAccessibilityPreferences(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    preferences,
    setPreference,
    togglePreference: key => setPreference(key, !preferences[key]),
    reduceMotion: preferences.reduzirAnimacoes,
  }), [preferences, setPreference]);

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (!context) throw new Error("useAccessibility deve ser usado dentro de AccessibilityProvider");
  return context;
}
